import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ContractFormValues } from '../schemas';

interface FinancialSectionProps {
  isFieldDisabled: boolean;
  selectedType: string;
  selectedCurrency: string;
}

export function FinancialSection({
  isFieldDisabled,
  selectedType,
  selectedCurrency,
}: FinancialSectionProps) {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          name="dolarRate"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dolarRate')} UZS</FormLabel>
              <FormControl>
                <NumericFormat
                  thousandSeparator=" "
                  allowNegative={false}
                  decimalScale={2}
                  customInput={Input}
                  disabled={isFieldDisabled}
                  placeholder={t('dolarRate')}
                  className="bg-white"
                  value={field.value ?? ''}
                  onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="currency"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('currency')}</FormLabel>
              <FormControl>
                <Select
                  disabled={isFieldDisabled}
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder={t('selectCurrency')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">{t('USD')}</SelectItem>
                    <SelectItem value="UZS">{t('UZS')}</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {(selectedType === 'FIXED' || selectedType === 'HOURLY') && (
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            name="advancePayment"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('advancePayment')}
                  {form.watch('advancePaymentUnit') === 'percent'
                    ? '%'
                    : selectedCurrency === 'USD'
                      ? ' $'
                      : ' UZS'}
                </FormLabel>
                <FormControl>
                  <NumericFormat
                    thousandSeparator=" "
                    allowNegative={false}
                    decimalScale={0}
                    customInput={Input}
                    // HOURLY always bills the tariff price up front, so the advance
                    // mirrors it and is not independently editable — keep the input
                    // read-only instead of silently discarding what the user types.
                    disabled={isFieldDisabled || selectedType === 'HOURLY'}
                    placeholder={t('advancePaymentPlaceholder')}
                    className="bg-white"
                    value={
                      selectedType === 'HOURLY'
                        ? (form.watch('hourlyPackage.price') ?? '')
                        : (field.value ?? '')
                    }
                    onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                  />
                </FormControl>
                {selectedType === 'HOURLY' && (
                  <p className="text-muted-foreground text-xs">
                    {t('advanceMirrorsTariffHint')}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {selectedType === 'FIXED' && (
            <FormField
              name="advancePaymentUnit"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('paymentUnit')} *</FormLabel>
                  <FormControl>
                    <Select
                      disabled={isFieldDisabled}
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder={t('selectPaymentUnit')} />
                      </SelectTrigger>
                      <SelectContent>
                        {/* `usd` is the "fixed amount" unit (vs percent) — the stored
                            value stays, but label it with the contract's own currency
                            so a UZS contract doesn't read "USD". */}
                        <SelectItem value="usd">
                          {selectedCurrency === 'UZS' ? t('UZS') : t('usd')}
                        </SelectItem>
                        <SelectItem value="percent">{t('percent')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
}
